import React, { useState, useRef, useEffect } from 'react';
import './UnifiedRoomLayout.css';

// Helper component to bind a MediaStream to a video element
const RemoteVideo = ({ stream, className }) => {
    const videoRef = useRef(null);

    useEffect(() => {
        if (videoRef.current && stream) {
            videoRef.current.srcObject = stream;
        }
    }, [stream]);

    return (
        <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={className}
        />
    );
};

/**
 * UnifiedRoomLayout - Single layout for both Conference and Presentation modes
 * Maintains exact same proportions and structure across both modes
 */
const UnifiedRoomLayout = ({
    // Mode
    mode = 'conference', // 'conference' | 'presentation'

    // Sidebar
    sidebarExpanded = true,
    onToggleSidebar,

    // My Video
    localVideoRef,
    currentUser,
    localVideoStyle,
    micOn,
    camOn,
    onMicToggle,
    onCamToggle,
    onOpenDeviceSettings,

    // Main Content
    mainContent,

    // Bottom Video Strip (Presentation mode)
    showBottomStrip = false,
    bottomStripExpanded = true,
    onToggleBottomStrip,
    otherParticipants = [],

    // Chat/Participants
    messages = [],
    participants = [],
    onSendMessage,

    // Presenter
    presenterId,

    // Header
    sessionTime = '0:00',
    onLeave,
    headerTitle = 'Virtual Café',

    // Bottom Toolbar
    bottomToolbar,
    localMicActive = false,
}) => {
    const [activeTab, setActiveTab] = useState('chat'); // 'chat' | 'participants'
    const [chatInput, setChatInput] = useState('');

    const handleSendMessage = (e) => {
        e.preventDefault();
        if (chatInput.trim() && onSendMessage) {
            onSendMessage(chatInput);
            setChatInput('');
        }
    };

    return (
        <div className={`unified-room-layout ${sidebarExpanded ? 'sidebar-expanded' : 'sidebar-collapsed'}`}>
            {/* HEADER */}
            <div className="url-header">
                <div className="url-header-left">
                    <span className="url-room-title">🎥 {headerTitle}</span>
                </div>
                <div className="url-header-right">
                    <span className="url-session-time">⏱️ {sessionTime}</span>
                    <button className="url-leave-btn" onClick={onLeave}>
                        🚪 Leave Room
                    </button>
                </div>
            </div>

            {/* MAIN CONTENT AREA */}
            <div className="url-main-content">
                <div className="url-main-inner">
                    {mainContent}
                </div>

                {/* Bottom Participant Strip (Presentation Mode Only) */}
                {showBottomStrip && (
                    <div className={`url-participant-strip ${bottomStripExpanded ? '' : 'collapsed'}`}>
                        {/* Toggle button */}
                        <button
                            className="url-strip-toggle"
                            onClick={onToggleBottomStrip}
                            aria-label={bottomStripExpanded ? 'Hide participants' : 'Show participants'}
                            title={bottomStripExpanded ? 'Hide participants' : 'Show participants'}
                        >
                            {bottomStripExpanded ? '▼' : '▲'}
                        </button>
                        {bottomStripExpanded && otherParticipants.length > 0 && otherParticipants.map((participant) => (
                            <div key={participant.id} className="url-strip-participant">
                                {participant.stream ? (
                                    <RemoteVideo stream={participant.stream} className="url-strip-video" />
                                ) : (
                                    <div className="url-strip-avatar">
                                        {participant.avatar || participant.name?.[0] || '?'}
                                    </div>
                                )}
                                <span className="url-strip-name">{participant.name}</span>
                            </div>
                        ))}
                        {bottomStripExpanded && otherParticipants.length === 0 && (
                            <span className="url-strip-empty">No other participants</span>
                        )}
                    </div>
                )}
            </div>

            {/* SIDEBAR */}
            <div className="url-sidebar">
                {/* Collapse/Expand Toggle */}
                <button
                    className="url-sidebar-toggle"
                    onClick={onToggleSidebar}
                    aria-label={sidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
                >
                    {sidebarExpanded ? '›' : '‹'}
                </button>

                {sidebarExpanded && (
                    <>
                        {/* MY VIDEO SECTION */}
                        <div className="url-my-video-section">
                            <div className={`url-my-video-container ${localMicActive && micOn ? 'mic-active' : ''}`}>
                                <video
                                    ref={localVideoRef}
                                    autoPlay
                                    playsInline
                                    muted
                                    className="url-my-video"
                                    style={localVideoStyle}
                                />
                                <span className="url-my-video-label">{currentUser || 'Me'}</span>
                            </div>

                            {/* Media Controls */}
                            <div className="url-media-controls">
                                <button
                                    className={`url-control-btn ${micOn ? 'active' : 'inactive'}`}
                                    onClick={onMicToggle}
                                    title={micOn ? 'Mute' : 'Unmute'}
                                >
                                    {micOn ? '🎤' : '🔇'}
                                </button>
                                <button
                                    className={`url-control-btn ${camOn ? 'active' : 'inactive'}`}
                                    onClick={onCamToggle}
                                    title={camOn ? 'Turn off camera' : 'Turn on camera'}
                                >
                                    {camOn ? '📷' : '📷'}
                                </button>
                                <button
                                    className="url-control-btn"
                                    onClick={onOpenDeviceSettings}
                                    title="Device Settings"
                                >
                                    ⚙️
                                </button>
                            </div>
                        </div>

                        {/* TAB NAVIGATION */}
                        <div className="url-tabs">
                            <button
                                className={`url-tab ${activeTab === 'chat' ? 'active' : ''}`}
                                onClick={() => setActiveTab('chat')}
                            >
                                💬 Chat
                            </button>
                            <button
                                className={`url-tab ${activeTab === 'participants' ? 'active' : ''}`}
                                onClick={() => setActiveTab('participants')}
                            >
                                👥 Participants ({participants.length})
                            </button>
                        </div>

                        {/* CHAT TAB */}
                        {activeTab === 'chat' && (
                            <div className="url-chat-section">
                                <div className="url-messages">
                                    {messages.map((msg, idx) => (
                                        <div key={idx} className="url-message">
                                            <span className="url-message-user">{msg.user}:</span>
                                            <span className="url-message-text">{msg.text}</span>
                                        </div>
                                    ))}
                                </div>
                                <form className="url-chat-input-form" onSubmit={handleSendMessage}>
                                    <input
                                        type="text"
                                        className="url-chat-input"
                                        placeholder="Type a message..."
                                        value={chatInput}
                                        onChange={(e) => setChatInput(e.target.value)}
                                    />
                                    <button type="submit" className="url-send-btn">➤</button>
                                </form>
                            </div>
                        )}

                        {/* PARTICIPANTS TAB */}
                        {activeTab === 'participants' && (
                            <div className="url-participants-section">
                                {participants.map((participant) => (
                                    <div
                                        key={participant.id}
                                        className={`url-participant-item ${presenterId && participant.id === presenterId ? 'presenter' : ''}`}
                                    >
                                        <div className="url-participant-badges">
                                            {presenterId && participant.id === presenterId && (
                                                <span className="url-participant-badge presenter">Presenter</span>
                                            )}
                                            {participant.isSelf && (
                                                <span className="url-participant-badge you">You</span>
                                            )}
                                        </div>
                                        <span className="url-participant-name">
                                            {participant.name}
                                        </span>
                                        <div className="url-participant-status">
                                            <span className={participant.micOn ? 'status-on' : 'status-off'}>
                                                {participant.micOn ? '🎤' : '🔇'}
                                            </span>
                                            <span className={participant.camOn ? 'status-on' : 'status-off'}>
                                                {participant.camOn ? '📷' : '📷'}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* TOOLBAR (Bottom) */}
            <div className="url-toolbar">
                {bottomToolbar}
            </div>
        </div>
    );
};

export default UnifiedRoomLayout;
